/**
 * PhoneFrame - reusable portrait mobile phone shell.
 * Used by VoiceFeaturesSection (passes its own status bar in children) and
 * GallerySection (uses the built-in status bar + Safari-style browser toolbar).
 *
 * Props:
 *   bezelColor        - CSS colour for the outer shell. Defaults to near-black.
 *   width             - Override width in px. Default: min(360px, 100%).
 *   minHeight         - Override screen min-height in px. Default: 560px.
 *   showDynamicIsland - Whether to render the small black circle. Default true.
 *   showHomeIndicator - Whether to render the bottom pill. Default true.
 *   showStatusBar     - Show built-in iOS-style status bar (time + signal/wifi/battery).
 *   statusBarTime     - String shown in the status bar. Default "9:41".
 *   showBrowserBar    - Show Safari-style URL toolbar under the status bar.
 *   browserUrl        - URL string for the toolbar. Default empty.
 *   screenBg          - 'dark' | 'light' — affects status bar + browser bar colours. Default 'light'.
 *   children          - The content rendered inside the screen body.
 */
import { useState, useEffect } from 'react';


function StatusBarBits({ time = '9:41', isDark, iconColor, compact = false }) {
  const fg = iconColor ? '' : (isDark ? 'text-white' : 'text-gray-900');
  const style = iconColor ? { color: iconColor } : undefined;
  // Battery rectangle borders use opacity, so we mirror with the chosen colour.
  const batteryBorder = iconColor || (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(17,24,39,0.7)');
  const batteryFill = iconColor || (isDark ? 'rgb(255,255,255)' : 'rgb(17,24,39)');
  // Compact mode: smaller icons + tighter spacing so the status bar doesn't
  // crash into the dynamic island on smaller phone mockups (used by the
  // gallery section).
  const wrapPad = compact ? 'px-5 pt-2 pb-0.5' : 'px-7 pt-3 pb-1';
  const textSize = compact ? 'text-[9px]' : 'text-[11px]';
  const iconGap = compact ? 'gap-1' : 'gap-1.5';
  const iconSize = compact ? 'w-3 h-2' : 'w-4 h-3';
  const batteryBox = compact ? 'w-5 h-2' : 'w-6 h-2.5';
  const batteryTip = compact ? 'w-[1.5px] h-1' : 'w-[2px] h-1.5';
  return (
    <div className={`flex items-center justify-between ${wrapPad} ${textSize} font-semibold ${fg}`} style={style}>
      <span className="tracking-tight" data-testid="phone-status-time">{time}</span>
      <div className={`flex items-center ${iconGap}`}>
        <svg viewBox="0 0 18 12" className={iconSize} aria-hidden="true">
          <rect x="0" y="8" width="3" height="4" rx="0.5" fill="currentColor" />
          <rect x="5" y="5" width="3" height="7" rx="0.5" fill="currentColor" />
          <rect x="10" y="2" width="3" height="10" rx="0.5" fill="currentColor" />
          <rect x="15" y="0" width="3" height="12" rx="0.5" fill="currentColor" />
        </svg>
        <svg viewBox="0 0 16 12" className={iconSize} aria-hidden="true">
          <path d="M8 11.5a1 1 0 100-2 1 1 0 000 2zM8 8.5c1.1 0 2.1.4 2.9 1.1l1.4-1.4A6 6 0 008 6.5a6 6 0 00-4.3 1.7l1.4 1.4A4 4 0 018 8.5zM8 5c2 0 3.8.7 5.2 2l1.4-1.5A9 9 0 008 3a9 9 0 00-6.6 2.5L2.8 7A8 8 0 018 5z" fill="currentColor" />
        </svg>
        <div className="flex items-center">
          <div className={`relative ${batteryBox} rounded-[3px] border`} style={{ borderColor: batteryBorder }}>
            <div className="absolute inset-0.5 rounded-[1.5px]" style={{ width: '75%', backgroundColor: batteryFill }} />
          </div>
          <div className={`${batteryTip} ml-px rounded-r-sm`} style={{ backgroundColor: batteryBorder }} />
        </div>
      </div>
    </div>
  );
}

function BrowserBar({ url, isDark, bg }) {
  const wrap = bg ? '' : (isDark ? 'bg-gray-900' : 'bg-gray-100');
  const wrapStyle = bg ? { backgroundColor: bg } : undefined;
  const pill = isDark ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-700 ring-1 ring-gray-200';
  const iconMute = isDark ? 'text-gray-400' : 'text-gray-500';
  return (
    <div className={`${wrap} px-3 pt-1.5 pb-2`} style={wrapStyle} data-testid="phone-browser-bar">
      <div className={`flex items-center gap-2 ${pill} rounded-xl px-3 py-1.5 text-[11px]`}>
        <span className={`shrink-0 text-[10px] font-semibold ${iconMute}`}>aA</span>
        <svg viewBox="0 0 12 14" className={`w-3 h-3.5 shrink-0 ${iconMute}`} aria-hidden="true" fill="currentColor">
          <path d="M6 0a4 4 0 00-4 4v2H1a1 1 0 00-1 1v6a1 1 0 001 1h10a1 1 0 001-1V7a1 1 0 00-1-1h-1V4a4 4 0 00-4-4zm-2.5 4a2.5 2.5 0 015 0v2h-5V4z" />
        </svg>
        <span className="flex-1 text-center truncate" data-testid="phone-browser-url">{url}</span>
        <svg viewBox="0 0 16 16" className={`w-3.5 h-3.5 shrink-0 ${iconMute}`} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 8a5 5 0 018.5-3.54L13 6" />
          <path d="M13 3v3h-3" />
          <path d="M13 8a5 5 0 01-8.5 3.54L3 10" />
          <path d="M3 13v-3h3" />
        </svg>
      </div>
    </div>
  );
}

// Compute live device time formatted as e.g. "9:41" (no seconds, no AM/PM).
function useLiveTime(enabled) {
  const [t, setT] = useState(() => formatNowHHmm());
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => setT(formatNowHHmm()), 30000);
    return () => clearInterval(id);
  }, [enabled]);
  return t;
}
function formatNowHHmm() {
  const d = new Date();
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function PhoneFrame({
  children,
  bezelColor,
  width,
  minHeight,
  maxHeight,
  showDynamicIsland = true,
  showHomeIndicator = true,
  showStatusBar = false,
  statusBarTime,
  statusBarTimeMode,
  statusBarBg,
  statusBarIconColor,
  compactStatusBar = false,
  showBrowserBar = false,
  browserUrl,
  browserBarBg,
  screenBg = 'light',
  dropShadow = false,
  floorShadow = false,
  floorShadowOffset = 0,
}) {
  const shellStyle = { borderRadius: '2.75rem' };
  if (width && Number(width) > 0) {
    shellStyle.width = `${width}px`;
    shellStyle.maxWidth = '100%';
  } else {
    shellStyle.width = 'min(360px, 100%)';
  }
  if (bezelColor) shellStyle.backgroundColor = bezelColor;
  if (dropShadow) {
    // "Sitting on a shiny surface" effect: short mirror reflection that fades
    // away from the phone. The -webkit-box-reflect mask is applied in the
    // original element's coordinate space *before* the reflection is flipped,
    // so to keep the phone's bottom visible (touching the mockup) and the
    // camera/island area invisible (far from the mockup), the gradient runs
    // bottom-to-top: opaque at the bottom of the phone, transparent above 22%.
    // Webkit-only (Chrome/Edge/Safari); Firefox falls back to the contact
    // shadow alone.
    shellStyle.WebkitBoxReflect = 'below 4px linear-gradient(to top, rgba(0,0,0,0.35), transparent 11%)';
    shellStyle.boxShadow = '0 6px 12px -2px rgba(0,0,0,0.32), 0 18px 28px -8px rgba(0,0,0,0.22)';
  }

  const screenStyle = { borderRadius: '2.1rem' };
  const effMin = (minHeight && Number(minHeight) > 0) ? Number(minHeight) : null;
  const effMax = (maxHeight && Number(maxHeight) > 0) ? Number(maxHeight) : null;
  if (effMax !== null) {
    // Mobile / capped mode — pin the phone to exactly this height. We avoid
    // setting any minHeight here because CSS resolves a min>max conflict in
    // favour of min, which would silently ignore the cap.
    screenStyle.height = `${effMax}px`;
    screenStyle.maxHeight = `${effMax}px`;
  } else if (effMin !== null) {
    screenStyle.minHeight = `${effMin}px`;
  } else {
    screenStyle.minHeight = '560px';
  }
  const isDark = screenBg === 'dark';
  const bodyBg = isDark ? 'bg-gray-900' : 'bg-white';
  const hasBars = showStatusBar || showBrowserBar;

  // Resolve which time string to show. Mode wins over literal `statusBarTime`
  // unless the mode is 'custom' (then `statusBarTime` is the user-typed value).
  const liveTime = useLiveTime(statusBarTimeMode === 'system');
  let displayTime = '9:41';
  if (statusBarTimeMode === 'system') displayTime = liveTime;
  else if (statusBarTimeMode === 'custom') displayTime = (statusBarTime && String(statusBarTime).trim()) || '9:41';
  else if (statusBarTimeMode === 'apple') displayTime = '9:41';
  else displayTime = statusBarTime || '9:41'; // legacy: literal string

  // Status bar wrapper background can be overridden; defaults to the legacy
  // light/dark grey block.
  const barsWrapClass = statusBarBg ? '' : (isDark ? 'bg-gray-900' : 'bg-gray-100');
  const barsWrapStyle = statusBarBg ? { backgroundColor: statusBarBg } : undefined;

  return (
    <div className="flex justify-center" data-testid="phone-frame">
      <div className="relative bg-gray-950 p-[10px] ring-1 ring-black/40" style={shellStyle}>
        {/* Cosmetic side buttons */}
        <div className="absolute -left-[3px] top-24 w-[3px] h-10 rounded-l bg-gray-800" />
        <div className="absolute -left-[3px] top-40 w-[3px] h-16 rounded-l bg-gray-800" />
        <div className="absolute -left-[3px] top-60 w-[3px] h-16 rounded-l bg-gray-800" />
        <div className="absolute -right-[3px] top-32 w-[3px] h-20 rounded-r bg-gray-800" />

        {/* Floor shadow — soft elliptical drop sitting just under the phone.
            Uses the canonical PNG asset (400x100, visible ellipse roughly at
            rows 65-93 of the source), scaled to ~115% of the phone width.
            The translateY pulls the asset up so the visible dark band lines
            up snugly with the phone's base instead of floating away from it.

            `aspectRatio` is set explicitly to the source PNG's 4:1 so the
            element reserves the correct height BEFORE the binary loads —
            otherwise the translateY (which is a % of the img's own height)
            evaluates to 0 on first paint and the shadow renders detached
            from the phone, then jumps into position once the PNG decodes.
            `loading="eager"` + `fetchPriority="high"` keep it from being
            deferred. Pointer-events disabled so it never blocks clicks. */}
        {floorShadow && (
          <img
            src="/static/phone-shadow.png"
            alt=""
            aria-hidden="true"
            loading="eager"
            fetchpriority="high"
            decoding="sync"
            width={400}
            height={100}
            className="pointer-events-none select-none absolute left-1/2 z-0"
            style={{
              top: '100%',
              width: '115%',
              height: 'auto',
              aspectRatio: '4 / 1',
              transform: `translate(-50%, calc(-62% + ${Number(floorShadowOffset) || 0}px))`,
            }}
            data-testid="phone-floor-shadow"
          />
        )}

        {/* Screen */}
        <div className={`relative overflow-hidden flex flex-col ${bodyBg}`} style={screenStyle}>
          {hasBars && (
            <div className={`shrink-0 ${barsWrapClass}`} style={barsWrapStyle}>
              {showStatusBar && <StatusBarBits time={displayTime} isDark={isDark} iconColor={statusBarIconColor} compact={compactStatusBar} />}
              {showBrowserBar && <BrowserBar url={browserUrl || ''} isDark={isDark} bg={browserBarBg} />}
            </div>
          )}
          <div className="relative flex-1 overflow-hidden">{children}</div>

          {/* Dynamic island sits above bars */}
          {showDynamicIsland && (
            <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-30 w-5 h-5 rounded-full bg-black" aria-hidden="true" />
          )}
          {showHomeIndicator && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-30 w-32 h-1 rounded-full bg-gray-500/70" aria-hidden="true" />
          )}
        </div>
      </div>
    </div>
  );
}

export default PhoneFrame;
